'use client';

import cls from 'classnames';
import { useState, useEffect } from 'react';
import { API_PUBLIC } from '@/services/ecommerce/apis/apiConstrant';
import Pagination from '@/components/atoms/Pagination';
import AroggaAPI from '@/services/ecommerce/apis/AroggaAPI';
import TopRatingLoader from './TopRatingLoader';
import ReviewContentLoader from './ReviewContentLoader';
import useProduct from '@/contexts/ProductProvider';
import ReviewCard from '@/components/molecules/ReviewCard/ReviewCard';
import RatingSummary from '@/components/molecules/RatingSummary';

interface ProductReviewSummary {
	id?: number;
	rs_average?: number;
	rs_five_star?: number;
	rs_four_star?: number;
	rs_id?: number;
	rs_one_star?: number;
	rs_product_variant_id?: number;
	rs_three_star?: number;
	rs_total?: number;
	rs_two_star?: number;
	summary?: any;
}

interface MemoizedFilter {
	_perPage?: number;
	_page?: number;
	_order?: string;
	_orderBy?: number | string | null;
	_rating?: number | string | null;
	_variant_id?: number;
	_has_media?: number;
}

const ITEMS_PER_PAGE = 10;

const RatingReviews = () => {
	const initialProductReviewSummary: ProductReviewSummary = {
		rs_average: 0,
		rs_five_star: 0,
		rs_four_star: 0,
		rs_id: 0,
		rs_one_star: 0,
		rs_product_variant_id: 0,
		rs_three_star: 0,
		rs_total: 0,
		rs_two_star: 0,
		summary: []
	};

	const { selectedVariant } = useProduct();
	const [sortBy, setSortBy] = useState('');
	const [ratingFilter, setRatingFilter] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [totalItems, setTotalItems] = useState(0);
	const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
	const [productReviews, setProductReviews] = useState<any>();
	const [productReviewSummary, setProductReviewSummary] = useState<ProductReviewSummary>(initialProductReviewSummary);
	const [isSummeryLodding, setIsSummeryLodding] = useState(false);
	const [isLodding, setIsLodding] = useState(false);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const memoizedFilter: MemoizedFilter = selectedVariant?.id
		? {
				_perPage: ITEMS_PER_PAGE,
				_page: currentPage,
				...(sortBy && {
					_order: sortBy === 'rating_high_to_low' ? 'desc' : sortBy === 'rating_low_to_high' ? 'asc' : sortBy,
					_orderBy:
						sortBy === 'rating_low_to_high' || sortBy === 'rating_high_to_low' ? 'pr_rating' : 'pr_modified_at'
				}),
				...(selectedVariant?.id && { _variant_id: selectedVariant?.id }), // Add _variant_id only when PvId changes
				...(ratingFilter === 'clear'
					? {} // Reset all filters
					: ratingFilter === 'hasMedia'
						? { _has_media: 1 }
						: ratingFilter
							? { _rating: ratingFilter }
							: {})
			}
		: {}; // Reset to empty object if PvId changes or becomes undefined.

	const fetchReviews = async (page = 1) => {
		try {
			setIsLodding(true);
			const {
				data: fetchedData,
				total,
				status
			}: any = await AroggaAPI.get(API_PUBLIC.PRODUCT_OWNER_REVIEW, { ...memoizedFilter, _page: page });

			if (status === 'fail') {
				setTotalItems(0);
				setProductReviews([]);
				return;
			}

			setProductReviews(fetchedData);
			setTotalItems(total || 0);
		} catch (error) {
			setTotalItems(0);
			setProductReviews([]);
		} finally {
			setIsLodding(false);
		}
	};

	const getReviewSummary = async () => {
		try {
			setIsSummeryLodding(true);
			const { data: summaryData, status }: any = await AroggaAPI.get(API_PUBLIC.REVIEW_SUMMERY, {
				_variant_id: selectedVariant?.id,
				_product_id: selectedVariant?.pv_p_id
			});

			if (status === 'fail') {
				return;
			}
			setProductReviewSummary(summaryData);
		} catch (error) {
		} finally {
			setIsSummeryLodding(false);
		}
	};

	useEffect(() => {
		if (selectedVariant?.id) {
			// Call getReviewSummary whenever PvId changes
			getReviewSummary();
			if (ratingFilter === 'clear') {
				setSortBy('');
				setRatingFilter('');
			} else {
				fetchReviews(currentPage);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedVariant, currentPage, sortBy, ratingFilter]);

	return (
		<div className={cls('bg-white rounded-10 border-1-grey300 rounded-10 mt-24 mb-24 p-20')} id='productReview'>
			{isSummeryLodding ? (
				<TopRatingLoader />
			) : (
				<RatingSummary
					reviewSummary={productReviewSummary}
					setSortBy={setSortBy}
					sortBy={sortBy}
					ratingFilter={ratingFilter}
					setRatingFilter={setRatingFilter}
				/>
			)}

			{isLodding ? (
				<p className='flex w-full justify-center align-center pt-20' style={{ flexDirection: 'column' }}>
					<ReviewContentLoader count={6} />
				</p>
			) : productReviews?.length > 0 ? (
				productReviews.map((review, itemIndex) => (
					<div key={itemIndex} className='pt-20 pb-20' style={{ borderBottom: '1px solid #EEEFF2' }}>
						<ReviewCard
							rating={review?.pr_rating}
							reviewText={review?.pr_review_text}
							images={review?.attachedFiles_pr_review_files}
							userName={review?.pr_user_name}
							createdDate={review?.pr_modified_at}
							isEditable={false}
							isDetails={true}
						/>
					</div>
				))
			) : (
				<p className='flex justify-center align-center pt-20'>No reviews found</p>
			)}

			{totalItems !== 0 && (
				<div className='pt-16' style={{ display: 'flex', justifyContent: 'end' }}>
					<Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
				</div>
			)}
		</div>
	);
};

export default RatingReviews;
